import { Button, Heading, List, ListRow, Spinner, Text } from '@ui';
import type { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';
import { formatDateTime } from '../../../lib/format';
import type { ForageSuggestion, ForageSuggestions, LandscapeEvidence } from '../api';
import { formatNumber } from '../format';
import styles from './forage.module.css';

/** Landscapes measured by area; the others are counted trees. */
const AREAS = new Set<LandscapeEvidence['kind']>([
  'orchard',
  'heath',
  'forest',
  'meadow',
  'rape_field',
  'sunflower_field',
  'phacelia_field',
  'maize_field',
]);

/**
 * "Rapsfelder 3,2 ha · 1,2 km entfernt", "2 Linden · 0,4 km entfernt" or
 * "Wald · am Standort" when the location lies within it.
 */
export function evidenceText(e: LandscapeEvidence, t: TFunction): string {
  const label = t(`forage.landscape.${e.kind}`, { count: e.count });
  const area = e.area_ha
    ? ` ${t('forage.suggestions.area', { area: formatNumber(e.area_ha) })}`
    : '';
  const what = AREAS.has(e.kind) ? `${label}${area}` : `${e.count} ${label}`;
  const where =
    e.nearest_km === 0
      ? t('forage.suggestions.here')
      : t('forage.suggestions.distance', { km: formatNumber(e.nearest_km) });
  return `${what} · ${where}`;
}

const clock = new Intl.DateTimeFormat('de-DE', { timeStyle: 'short' });

/** "14:30 Uhr" today, "morgen, 14:30 Uhr" tomorrow, otherwise with the date. */
function when(value: string, t: TFunction): string {
  const date = new Date(value);
  const tomorrow = new Date();
  tomorrow.setDate(tomorrow.getDate() + 1);
  if (date.toDateString() === new Date().toDateString()) return `${clock.format(date)} Uhr`;
  if (date.toDateString() === tomorrow.toDateString())
    return t('forage.suggestions.tomorrow', { time: clock.format(date) });
  return `${formatDateTime(date)} Uhr`;
}

/**
 * Plants found around the location that are not chosen yet (TRA-007), each
 * with what was found and a button to add it. Nothing when switched off.
 * When the map source failed: why, when it is tried again, and a button to
 * try now.
 */
export function SuggestionGroup({
  suggestions,
  chosen,
  onAdd,
  onRetry,
  retrying = false,
}: {
  suggestions: ForageSuggestions | undefined;
  chosen: Set<string>;
  onAdd: (suggestion: ForageSuggestion) => void;
  onRetry?: (() => void) | undefined;
  retrying?: boolean;
}) {
  const { t } = useTranslation();
  if (!suggestions || suggestions.state === 'off') return null;
  if (suggestions.state === 'pending')
    return (
      <div className={styles.suggestionStatus} role="status">
        <Spinner size="sm" />
        <Text size="sm" tone="muted">
          {t('forage.suggestions.pending')}
        </Text>
      </div>
    );
  if (suggestions.state === 'unavailable')
    return (
      <div className={styles.suggestionFailed} role="status">
        <Text size="sm" tone="muted">
          {suggestions.failed_reason
            ? t(`forage.suggestions.failed.${suggestions.failed_reason}`)
            : t('forage.suggestions.unavailable')}
          {suggestions.retry_at &&
            ` ${t('forage.suggestions.retryAt', { time: when(suggestions.retry_at, t) })}`}
        </Text>
        {onRetry && (
          <Button variant="secondary" size="sm" disabled={retrying} onClick={onRetry}>
            {t('forage.suggestions.retry')}
          </Button>
        )}
      </div>
    );
  const items = suggestions.items.filter((s) => !chosen.has(s.plant.id));
  if (items.length === 0) return null;
  return (
    <section className={styles.suggestions} aria-labelledby="forage-suggestions">
      <Heading level={3} size="sm" id="forage-suggestions">
        {t('forage.suggestions.title')}
      </Heading>
      <List>
        {items.map((s) => (
          <ListRow
            key={s.plant.id}
            title={<strong>{s.plant.name}</strong>}
            description={s.evidence.map((e) => evidenceText(e, t)).join(' · ')}
            trailing={
              <Button
                variant="secondary"
                size="sm"
                aria-label={t('forage.suggestions.add', { name: s.plant.name })}
                onClick={() => onAdd(s)}
              >
                {t('forage.suggestions.addShort')}
              </Button>
            }
          />
        ))}
      </List>
      {suggestions.attribution && (
        <Text size="xs" tone="muted">
          {t('forage.suggestions.source', {
            radius: formatNumber(suggestions.radius_km),
            source: suggestions.attribution,
          })}
        </Text>
      )}
    </section>
  );
}
